import React from 'react';
import { css } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import {
	type AGENT_AVATAR_CLIP_PATH as AgentAvatarClipPath,
	AgentAvatar,
} from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import IconTile from '@atlaskit/icon/icon-tile';
import { token } from '@atlaskit/tokens';

import { expVal } from '@confluence/feature-experiments';
import { LoadableAfterPaint, type LoadableType } from '@confluence/loadable';

import { type AgentIconType } from './agentIconType.ts';
import type { VisualStudioCodePresenceIconProps } from './VisualStudioCodePresenceIcon.tsx';
import type { MicrosoftCopilotPresenceIconProps } from './MicrosoftCopilotPresenceIcon.tsx';
import type { KiroPresenceIconProps } from './KiroPresenceIcon.tsx';
import type { HermesAgentPresenceIconProps } from './HermesAgentPresenceIcon.tsx';
import type { GrokPresenceIconProps } from './GrokPresenceIcon.tsx';
import type { GeminiPresenceIconProps } from './GeminiPresenceIcon.tsx';
import type { DiaPresenceIconProps } from './DiaPresenceIcon.tsx';
import type { AntigravityPresenceIconProps } from './AntigravityPresenceIcon.tsx';
import type { AmplitudePresenceIconProps } from './AmplitudePresenceIcon.tsx';
import type { CanvaPresenceIconProps } from './CanvaPresenceIcon.tsx';
import type { ChatGptIconProps } from './ChatGptIcon.tsx';
import type { ChatGptPresenceIconProps } from './ChatGptPresenceIcon.tsx';
import type { ClaudeIconProps } from './ClaudeIcon.tsx';
import type { ClaudePresenceIconProps } from './ClaudePresenceIcon.tsx';
import type { CodexIconProps } from './CodexIcon.tsx';
import type { CodexPresenceIconProps } from './CodexPresenceIcon.tsx';
import type { CortexPresenceIconProps } from './CortexPresenceIcon.tsx';
import type { CursorIconProps } from './CursorIcon.tsx';
import type { CursorPresenceIconProps } from './CursorPresenceIcon.tsx';
import type { DatabricksPresenceIconProps } from './DatabricksPresenceIcon.tsx';
import type { DiaIconProps } from './DiaIcon.tsx';
import type { ExternalPresenceIconProps } from './ExternalPresenceIcon.tsx';
import type { FigmaPresenceIconProps } from './FigmaPresenceIcon.tsx';
import type { FirstPartyAgentPresenceIconProps } from './FirstPartyAgentPresenceIcon.tsx';
import type { GitHubCopilotIconProps } from './GitHubCopilotIcon.tsx';
import type { GitHubCopilotPresenceIconProps } from './GitHubCopilotPresenceIcon.tsx';
import type { GrokIconProps } from './GrokIcon.tsx';
import type { HermesAgentIconProps } from './HermesAgentIcon.tsx';
import type { KiroIconProps } from './KiroIcon.tsx';
import type { LovablePresenceIconProps } from './LovablePresenceIcon.tsx';
import type { ManusPresenceIconProps } from './ManusPresenceIcon.tsx';
import type { OpenAiPresenceIconProps } from './OpenAiPresenceIcon.tsx';
import type { OpenCodeIconProps } from './OpenCodeIcon.tsx';
import type { ReplitPresenceIconProps } from './ReplitPresenceIcon.tsx';
import type { RovoPresenceIconProps } from './RovoPresenceIcon.tsx';
import type { SlackPresenceIconProps } from './SlackPresenceIcon.tsx';
import type { VisualStudioCodeIconProps } from './VisualStudioCodeIcon.tsx';
import { RovoIcon } from './RovoIcon.tsx';

export type AgentAvatarSize = 'xxsmall' | 'small' | 'medium';

/**
 * The Cursor icon inlines a large base64 raster, so it is code-split into its own
 * chunk (`loadable-CursorIcon`) and loaded after paint rather than statically
 * imported — it only renders for the rare `agentIcon === 'cursor'` case, so its
 * payload should not weigh down every chunk that includes this module.
 */
const CursorIcon: LoadableType<CursorIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CursorIcon" */ './CursorIcon.tsx')).CursorIcon,
});

const CursorPresenceIcon: LoadableType<CursorPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CursorPresenceIcon" */ './CursorPresenceIcon.tsx'))
			.CursorPresenceIcon,
});

const ExternalPresenceIcon: LoadableType<ExternalPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ExternalPresenceIcon" */ './ExternalPresenceIcon.tsx'
			)
		).ExternalPresenceIcon,
});

const CanvaPresenceIcon: LoadableType<CanvaPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CanvaPresenceIcon" */ './CanvaPresenceIcon.tsx'))
			.CanvaPresenceIcon,
});

/**
 * The Claude, ChatGPT and Figma brand icons only render for the rare
 * `agentIcon === 'claude'` / `'chatgpt'` / `'figma'` cases, so — like the Cursor icon —
 * they are code-split into their own chunks and loaded after paint rather than weighing
 * down every chunk that includes this module.
 */
const ClaudeIcon: LoadableType<ClaudeIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ClaudeIcon" */ './ClaudeIcon.tsx')).ClaudeIcon,
});

/**
 * Presence tiles render inside LazyAnalyticsDialog (LAZY). LoadablePaint here
 * crashes the byline dialog: "ClaudePresenceIcon [PAINT] has a higher priority
 * of it's parent component LazyAnalyticsDialog[LAZY]". Match Figma/Lovable and
 * load after paint.
 */
const ClaudePresenceIcon: LoadableType<ClaudePresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ClaudePresenceIcon" */ './ClaudePresenceIcon.tsx'))
			.ClaudePresenceIcon,
});

const ChatGptIcon: LoadableType<ChatGptIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ChatGptIcon" */ './ChatGptIcon.tsx')).ChatGptIcon,
});

const FigmaPresenceIcon: LoadableType<FigmaPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FigmaPresenceIcon" */ './FigmaPresenceIcon.tsx'))
			.FigmaPresenceIcon,
});

const LovablePresenceIcon: LoadableType<LovablePresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LovablePresenceIcon" */ './LovablePresenceIcon.tsx'
			)
		).LovablePresenceIcon,
});

const ReplitPresenceIcon: LoadableType<ReplitPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ReplitPresenceIcon" */ './ReplitPresenceIcon.tsx'))
			.ReplitPresenceIcon,
});

/**
 * The Dia and Codex icons only render for the rare `agentIcon === 'dia'` / `'codex'`
 * cases, so — like the other brand icons — they are code-split into their own chunks and
 * loaded after paint.
 */
const DiaIcon: LoadableType<DiaIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-DiaIcon" */ './DiaIcon.tsx')).DiaIcon,
});

const CodexIcon: LoadableType<CodexIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CodexIcon" */ './CodexIcon.tsx')).CodexIcon,
});

const CodexPresenceIcon: LoadableType<CodexPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CodexPresenceIcon" */ './CodexPresenceIcon.tsx'))
			.CodexPresenceIcon,
});

const RovoPresenceIcon: LoadableType<RovoPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-RovoPresenceIcon" */ './RovoPresenceIcon.tsx'))
			.RovoPresenceIcon,
});

const GrokIcon: LoadableType<GrokIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-GrokIcon" */ './GrokIcon.tsx')).GrokIcon,
});

const GitHubCopilotIcon: LoadableType<GitHubCopilotIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-GitHubCopilotIcon" */ './GitHubCopilotIcon.tsx'))
			.GitHubCopilotIcon,
});

const VisualStudioCodeIcon: LoadableType<VisualStudioCodeIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-VisualStudioCodeIcon" */ './VisualStudioCodeIcon.tsx'
			)
		).VisualStudioCodeIcon,
});

const OpenCodeIcon: LoadableType<OpenCodeIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-OpenCodeIcon" */ './OpenCodeIcon.tsx'))
			.OpenCodeIcon,
});

const HermesAgentIcon: LoadableType<HermesAgentIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-HermesAgentIcon" */ './HermesAgentIcon.tsx'))
			.HermesAgentIcon,
});

const KiroIcon: LoadableType<KiroIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-KiroIcon" */ './KiroIcon.tsx')).KiroIcon,
});

const FirstPartyAgentPresenceIcon: LoadableType<FirstPartyAgentPresenceIconProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-FirstPartyAgentPresenceIcon" */ './FirstPartyAgentPresenceIcon.tsx'
				)
			).FirstPartyAgentPresenceIcon,
	});

const AmplitudePresenceIcon: LoadableType<AmplitudePresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AmplitudePresenceIcon" */ './AmplitudePresenceIcon.tsx'
			)
		).AmplitudePresenceIcon,
});

const ChatGptPresenceIcon: LoadableType<ChatGptPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ChatGptPresenceIcon" */ './ChatGptPresenceIcon.tsx'
			)
		).ChatGptPresenceIcon,
});

const CortexPresenceIcon: LoadableType<CortexPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CortexPresenceIcon" */ './CortexPresenceIcon.tsx'))
			.CortexPresenceIcon,
});

const DatabricksPresenceIcon: LoadableType<DatabricksPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-DatabricksPresenceIcon" */ './DatabricksPresenceIcon.tsx'
			)
		).DatabricksPresenceIcon,
});

const GitHubCopilotPresenceIcon: LoadableType<GitHubCopilotPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-GitHubCopilotPresenceIcon" */ './GitHubCopilotPresenceIcon.tsx'
			)
		).GitHubCopilotPresenceIcon,
});

const ManusPresenceIcon: LoadableType<ManusPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ManusPresenceIcon" */ './ManusPresenceIcon.tsx'))
			.ManusPresenceIcon,
});

const OpenAiPresenceIcon: LoadableType<OpenAiPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-OpenAiPresenceIcon" */ './OpenAiPresenceIcon.tsx'))
			.OpenAiPresenceIcon,
});

const SlackPresenceIcon: LoadableType<SlackPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SlackPresenceIcon" */ './SlackPresenceIcon.tsx'))
			.SlackPresenceIcon,
});

const AntigravityPresenceIcon: LoadableType<AntigravityPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AntigravityPresenceIcon" */ './AntigravityPresenceIcon.tsx'
			)
		).AntigravityPresenceIcon,
});

const DiaPresenceIcon: LoadableType<DiaPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-DiaPresenceIcon" */ './DiaPresenceIcon.tsx'))
			.DiaPresenceIcon,
});

const GeminiPresenceIcon: LoadableType<GeminiPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-GeminiPresenceIcon" */ './GeminiPresenceIcon.tsx'))
			.GeminiPresenceIcon,
});

const GrokPresenceIcon: LoadableType<GrokPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-GrokPresenceIcon" */ './GrokPresenceIcon.tsx'))
			.GrokPresenceIcon,
});

const HermesAgentPresenceIcon: LoadableType<HermesAgentPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-HermesAgentPresenceIcon" */ './HermesAgentPresenceIcon.tsx'
			)
		).HermesAgentPresenceIcon,
});

const KiroPresenceIcon: LoadableType<KiroPresenceIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-KiroPresenceIcon" */ './KiroPresenceIcon.tsx'))
			.KiroPresenceIcon,
});

const MicrosoftCopilotPresenceIcon: LoadableType<MicrosoftCopilotPresenceIconProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-MicrosoftCopilotPresenceIcon" */ './MicrosoftCopilotPresenceIcon.tsx'
				)
			).MicrosoftCopilotPresenceIcon,
	});

const VisualStudioCodePresenceIcon: LoadableType<VisualStudioCodePresenceIconProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-VisualStudioCodePresenceIcon" */ './VisualStudioCodePresenceIcon.tsx'
				)
			).VisualStudioCodePresenceIcon,
	});

const unidentifiedAgentAvatarBackgroundStyles = css({
	// Scope Tile's default neutral background to the exact Figma value for this avatar only.
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/design-system/no-unsafe-design-token-usage
	'--ds-background-neutral': '#DDDEE1',
});

const AGENT_AVATAR_CLIP_PATH =
	'polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)' as const satisfies typeof AgentAvatarClipPath;

const styles = cssMap({
	xxsmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		flexShrink: 0,
	},
	compactInlineSpacing: {
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	small: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	medium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
	},
	iconWrapperSmallNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
	},
	iconWrapperMediumNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
	},
	unidentifiedAgentAvatarSmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarMedium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarSmallNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarMediumNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	brandAgentTile: {
		// Gray hexagon tile behind a brand mark (Figma/Dia/Codex), matching the gray
		// unidentified-agent avatar so brand icons read as the same hexagon shape.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
		backgroundColor: token('color.background.neutral'),
	},
	// Claude/Codex/Rovo Presence tiles draw Figma's hex + white stroke in SVG.
	// A CSS hex clip would crop that stroke.
	presenceGlyphTile: {
		// Keep inline SVG baseline spacing from shifting the glyph above the avatar's center.
		display: 'flex',
		overflow: 'visible',
	},
	avatarOffsetInlineStart: {
		transform: 'translateX(2px)',
	},
});

const AgentAvatarContainer = ({
	avatarSize,
	hasInlineSpacing,
	children,
}: {
	avatarSize: AgentAvatarSize;
	hasInlineSpacing: boolean;
	children: React.ReactNode;
}): React.JSX.Element => {
	if (avatarSize === 'xxsmall') {
		return (
			<Box xcss={cx(styles.xxsmall, hasInlineSpacing && styles.compactInlineSpacing)}>
				{children}
			</Box>
		);
	}

	if (avatarSize === 'medium' && hasInlineSpacing) {
		return <Box xcss={styles.medium}>{children}</Box>;
	}

	if (avatarSize === 'medium') {
		return <Box xcss={styles.iconWrapperMediumNoSpacing}>{children}</Box>;
	}

	if (hasInlineSpacing) {
		return <Box xcss={styles.small}>{children}</Box>;
	}

	return <Box xcss={styles.iconWrapperSmallNoSpacing}>{children}</Box>;
};

const UnidentifiedAgentAvatar = ({
	avatarSize,
	hasInlineSpacing,
}: {
	avatarSize: AgentAvatarSize;
	hasInlineSpacing: boolean;
}): React.JSX.Element => {
	if (avatarSize === 'xxsmall') {
		// IconTile starts at 20px; compose the compact 16px hexagon with the native small icon.
		return (
			<Box
				testId="unidentified-agent-avatar"
				xcss={cx(
					styles.xxsmall,
					styles.brandAgentTile,
					hasInlineSpacing && styles.compactInlineSpacing,
				)}
			>
				<AiAgentIcon label="" size="small" color={token('color.icon.subtle')} />
			</Box>
		);
	}

	const icon = (
		<span
			data-testid="unidentified-agent-avatar-background"
			css={unidentifiedAgentAvatarBackgroundStyles}
		>
			<IconTile icon={AiAgentIcon} label="" appearance="gray" size={avatarSize} />
		</span>
	);

	if (avatarSize === 'medium' && hasInlineSpacing) {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarMedium}>
				{icon}
			</Box>
		);
	}

	if (avatarSize === 'medium') {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarMediumNoSpacing}>
				{icon}
			</Box>
		);
	}

	if (hasInlineSpacing) {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarSmall}>
				{icon}
			</Box>
		);
	}

	return (
		<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarSmallNoSpacing}>
			{icon}
		</Box>
	);
};

/**
 * Renders an agent row's leading icon: the fixed AiAgent/Rovo icon for agents that
 * couldn't be resolved to an account, otherwise the deterministic hexagon AgentAvatar
 * (or, when the resolved agent account has a `picture`, that avatar image inside the
 * hexagon).
 */
export const AgentRowAvatar = ({
	name,
	agentIcon,
	avatarUrl,
	avatarSize = 'small',
	markSize,
	isOffsetInlineStart = false,
	hasInlineSpacing = true,
}: {
	name?: string;
	agentIcon?: AgentIconType;
	avatarUrl?: string;
	avatarSize?: AgentAvatarSize;
	markSize?: 16 | 20;
	isOffsetInlineStart?: boolean;
	hasInlineSpacing?: boolean;
}): React.JSX.Element => {
	const iconSize = { xxsmall: 16, small: 24, medium: 32 }[avatarSize];
	const iconMarkSize = markSize ?? iconSize;
	const presenceTile = (icon: React.JSX.Element): React.JSX.Element => (
		<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
			<Box xcss={styles.presenceGlyphTile} testId="presence-brand-tile">
				{icon}
			</Box>
		</AgentAvatarContainer>
	);
	const avatar = (() => {
		if (agentIcon === 'ai-agent' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<ExternalPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'ai-agent') {
			return (
				<UnidentifiedAgentAvatar avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing} />
			);
		}
		if (agentIcon === 'rovo' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<RovoPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'rovo') {
			return (
				<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
					<RovoIcon label="" size={iconMarkSize} />
				</AgentAvatarContainer>
			);
		}
		if (agentIcon === 'cursor' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<CursorPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'cursor') {
			return (
				<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
					<CursorIcon label="" size={iconMarkSize} />
				</AgentAvatarContainer>
			);
		}
		if (agentIcon === 'canva' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<CanvaPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'claude' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<ClaudePresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'claude') {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
					)}
				>
					<ClaudeIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'chatgpt' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<ChatGptPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'antigravity' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<AntigravityPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'dia' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<DiaPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'gemini' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<GeminiPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'grok' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<GrokPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'hermes' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<HermesAgentPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'kiro' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<KiroPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'microsoft-copilot' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<MicrosoftCopilotPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'vscode' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<VisualStudioCodePresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'chatgpt') {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
					)}
				>
					<ChatGptIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'figma' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<FigmaPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'lovable' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<LovablePresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'replit' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<ReplitPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'dia') {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<DiaIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'codex' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<CodexPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'first-party' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<FirstPartyAgentPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'amplitude' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<AmplitudePresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'cortex' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<CortexPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'databricks' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<DatabricksPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'github-copilot' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<GitHubCopilotPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'manus' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<ManusPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'openai' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<OpenAiPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'slack' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
			return presenceTile(<SlackPresenceIcon label="" size={iconSize} />);
		}
		if (agentIcon === 'codex') {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<CodexIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'grok' && expVal('ai_analytics_agent_types', 'isEnabled', false)) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<GrokIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'github-copilot' && expVal('ai_analytics_agent_types', 'isEnabled', false)) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<GitHubCopilotIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'vscode' && expVal('ai_analytics_agent_types', 'isEnabled', false)) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<VisualStudioCodeIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'open-code' && expVal('ai_analytics_agent_types', 'isEnabled', false)) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<OpenCodeIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'hermes' && expVal('ai_analytics_agent_types', 'isEnabled', false)) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<HermesAgentIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		if (agentIcon === 'kiro' && expVal('ai_analytics_agent_types', 'isEnabled', false)) {
			return (
				<Box
					xcss={cx(
						styles[avatarSize],
						avatarSize === 'xxsmall' && hasInlineSpacing && styles.compactInlineSpacing,
						styles.brandAgentTile,
					)}
				>
					<KiroIcon label="" size={iconMarkSize} />
				</Box>
			);
		}
		return (
			<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
				<AgentAvatar size={avatarSize} name={name} label="" imageUrl={avatarUrl} />
			</AgentAvatarContainer>
		);
	})();

	return isOffsetInlineStart ? (
		<Box testId="agent-avatar-offset" xcss={styles.avatarOffsetInlineStart}>
			{avatar}
		</Box>
	) : (
		avatar
	);
};
